import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Shield, Eye, Database, Lock, Trash2, Globe } from 'lucide-react';

export default function Privacy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-canvas">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>

        <h1 className="text-4xl font-bold text-ink mb-4">Politique de Confidentialité</h1>
        <p className="text-lg text-ink-muted mb-8">
          Spécifique au recrutement assisté par IA
        </p>
        
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-8">
          <h3 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Engagement RGPD
          </h3>
          <p className="text-amber-700">
            Ema est conforme au Règlement Général sur la Protection des Données (RGPD). 
            Nous traitons vos données de manière transparente, sécurisée et éthique.
          </p>
        </div>

        <div className="prose prose-zinc max-w-none">
          <p className="text-sm text-ink-muted mb-8">
            Dernière mise à jour : Février 2026
          </p>

          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Database className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-2xl font-semibold text-ink">1. Données Collectées</h2>
            </div>
            
            <h3 className="text-lg font-medium text-ink mb-2">Données Recruteurs</h3>
            <ul className="list-disc pl-6 text-ink-muted space-y-1 mb-4">
              <li>Email professionnel et nom</li>
              <li>Nom de l'entreprise</li>
              <li>Informations de paiement (via Stripe)</li>
              <li>Historique d'utilisation</li>
            </ul>

            <h3 className="text-lg font-medium text-ink mb-2">Données Candidats</h3>
            <ul className="list-disc pl-6 text-ink-muted space-y-1">
              <li>Nom, email, téléphone</li>
              <li>CV et lettre de motivation</li>
              <li>Enregistrement vidéo des 2 premières minutes</li>
              <li>Transcription de l'entretien</li>
              <li>Scores d'évaluation IA</li>
            </ul>
          </section>

          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Eye className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-2xl font-semibold text-ink">2. Utilisation des Données</h2>
            </div>
            <ul className="list-disc pl-6 text-ink-muted space-y-2">
              <li><strong>Évaluation IA :</strong> Analyse automatisée des compétences</li>
              <li><strong>Scoring :</strong> Génération de KPIs (hard skills, soft skills, vibe check)</li>
              <li><strong>Rapports :</strong> Création de rapports pour les recruteurs</li>
              <li><strong>Amélioration du service :</strong> Entraînement des modèles IA (données anonymisées)</li>
            </ul>
          </section>

          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Lock className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-2xl font-semibold text-ink">3. Sécurité des Données</h2>
            </div>
            <ul className="list-disc pl-6 text-ink-muted space-y-2">
              <li>Chiffrement des données en transit (TLS 1.3)</li>
              <li>Chiffrement au repos (AES-256)</li>
              <li>Stockage sur serveurs sécurisés (Supabase)</li>
              <li>Accès restreint aux employés autorisés</li>
              <li>Audit de sécurité régulier</li>
            </ul>
          </section>

          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-2xl font-semibold text-ink">4. Rétention et Suppression</h2>
            </div>
            <ul className="list-disc pl-6 text-ink-muted space-y-2">
              <li>Données d'entretien : 12 mois après le dernier accès</li>
              <li>Vidéos : 6 mois après l'entretien</li>
              <li>Comptes inactifs : Suppression après 24 mois</li>
              <li>Droit à l'effacement : Sur demande sous 30 jours</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-ink mb-4">5. Droits des Candidats</h2>
            <div className="bg-zinc-50 rounded-lg p-6">
              <p className="text-ink-muted mb-4">
                <strong className="text-ink">Important :</strong> Les candidats n'ont jamais accès à leurs scores 
                ou évaluations générés par l'IA. Cette information est strictement réservée aux recruteurs.
              </p>
              <p className="text-ink-muted">
                Les candidats peuvent exercer leurs droits RGPD (accès, rectification, suppression) 
                en contactant le recruteur ou directement Ema.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-ink mb-4">6. Partage de Données</h2>
            <p className="text-ink-muted mb-4">
              Nous ne vendons jamais vos données. Nous pouvons les partager avec :
            </p>
            <ul className="list-disc pl-6 text-ink-muted space-y-2">
              <li><strong>Stripe :</strong> Pour le traitement des paiements</li>
              <li><strong>Supabase :</strong> Pour le stockage sécurisé</li>
              <li><strong>Google (Gemini) :</strong> Pour l'analyse IA</li>
              <li><strong>Resend :</strong> Pour l'envoi d'emails</li>
            </ul>
          </section>

          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Globe className="w-5 h-5 text-amber-600" />
              </div>
              <h2 className="text-2xl font-semibold text-ink">7. Transferts Internationaux</h2>
            </div>
            <p className="text-ink-muted">
              Certaines données peuvent être traitées hors de l'UE (notamment via les services cloud). 
              Ces transferts sont encadrés par les Clauses Contractuelles Types de l'UE.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-ink mb-4">8. Cookies</h2>
            <p className="text-ink-muted mb-4">
              Nous utilisons des cookies essentiels pour le fonctionnement du service :
            </p>
            <ul className="list-disc pl-6 text-ink-muted space-y-1">
              <li>Authentification de session</li>
              <li>Préférences utilisateur</li>
              <li>Analyse anonyme d'utilisation</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-ink mb-4">9. Contact DPO</h2>
            <p className="text-ink-muted">
              Pour toute question relative à vos données personnelles :
            </p>
            <p className="mt-2">
              <strong className="text-ink">Délégué à la Protection des Données</strong><br />
              <a href="mailto:dpo@ema.ai" className="text-brand-primary hover:underline">
                dpo@ema.ai
              </a>
            </p>
          </section>

          <section className="border-t border-zinc-200 pt-8">
            <p className="text-sm text-ink-muted">
              En utilisant Ema, vous reconnaissez avoir lu et accepté cette politique de confidentialité.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
